import { Component, Mixins, Watch } from "vue-property-decorator";
import { mapActions, mapGetters } from "vuex";
import UserForm from "./components/UserForm/UserForm.vue";
import NotificationAboutAddNewUser from "./components/NotificationAboutAddNewUser/NotificationAboutAddNewUser.vue";
import ButtonWithIcon from "@/components/ButtonWithIcon/ButtonWithIcon.vue";
import { PageMixin } from "@/mixins/pageMixin";
import ButtonsSaveCancel from "@/components/Buttons/ButtonsSaveCancel.vue";
import DeleteUserModal from "@/views/UserProfile/components/DeleteUserModal/DeleteUserModal.vue";
import User from "@/vuex/models/user/user";
import { ActionTypes as UserActions } from "@/vuex/modules/users-module/action-types";
import { UserSearchParams } from "@/vuex/modules/users-module/actions";
import Meta from "@/vuex/models/meta";
import { Debounce } from "vue-debounce-decorator";
import AccessModal from "@/components/Modal/components/AccessModal.vue";
import InputSelectAccess from "@/components/InputSelectAccess/InputSelectAccess.vue";
import { ActionTypes } from "@/vuex/modules/organisation-requests-module/action-types";
import { OrganisationRequestSearchParams } from "@/vuex/modules/organisation-requests-module/actions";
import OrganisationRequest from "@/vuex/models/org-request/org-request";
import UsersTable from "@/views/UsersView/components/DataTables/UsersTable.vue";
import OrganisationRequestsTable from "@/views/UsersView/components/DataTables/OrganisationRequestsTable.vue";
import { ActionTypes as NotificationActions } from "@/vuex/modules/notifications-module/action-types";
import { NotificationType } from "@/vuex/modules/notifications-module/state";
import UserData from "@/vuex/models/user/user-data";

@Component({
  components: {
    OrganisationRequestsTable,
    UsersTable,
    InputSelectAccess,
    AccessModal,
    ButtonsSaveCancel,
    DeleteUserModal,
    UserForm,
    ButtonWithIcon,
    NotificationAboutAddNewUser,
  },
  computed: mapGetters({
    users: "users",
    usersMeta: "usersMeta",
    requests: "organisationRequests",
    orgRequestsMeta: "organisationRequestsMeta",
  }),
  methods: mapActions({
    fetchUsers: UserActions.FETCH_USERS,
    fetchRequests: ActionTypes.FETCH_REQUESTS,
    allowUser: UserActions.ALLOW_USER,
    denyUser: UserActions.DENY_USER,
  }),
})
export default class UsersView extends Mixins(PageMixin) {
  private readonly fetchUsers!: (params: UserSearchParams) => Promise<void>;
  private readonly fetchRequests!: (
    params: OrganisationRequestSearchParams
  ) => Promise<void>;
  private readonly allowUser!: (params: {
    userId: number;
    host: string;
  }) => Promise<void>;
  private readonly denyUser!: (params: {
    userId: number;
    host: string;
  }) => Promise<void>;

  protected readonly users!: Array<User>;
  protected readonly requests!: OrganisationRequest[];
  protected readonly usersMeta!: Meta;
  protected readonly orgRequestsMeta!: Meta;

  protected notificationAboutAddNewUserData: null | UserData = null;
  protected typeModal: string = "";
  protected activeTab: number = 1;
  protected showAccessModal: boolean = false;
  protected selectedUser: User | null = null;
  protected isOpenNotificationModal: boolean = false;

  private userSearchParams: UserSearchParams = {
    page: 1,
    per_page: 25,
    column: null,
    direction: null,
    search: null,
    organisation_id: null,
    role: null,
    locations_ids: [],
  };

  protected orgRequestSearchParams: OrganisationRequestSearchParams = {
    page: 1,
    per_page: 25,
    direction: null,
    column: null,
    search: null,
  };

  protected get hasAccessToOrgRequests(): boolean {
    return (
      this.auth.isSystemAdmin(this.me) || this.auth.isOrganisationAdmin(this.me)
    );
  }

  protected setNewUserDataHandler(userData: UserData) {
    this.notificationAboutAddNewUserData = userData;
    this.showForm = false;
  }

  protected get tableIsEmpty(): boolean {
    switch (this.activeTab) {
      case 1:
        return this.users.length < 1;
      case 2:
        return this.requests.length < 1;
      default:
        return false;
    }
  }

  protected get limitReached(): boolean {
    if (!this.auth.isSystemAdmin(this.me) && !!this.me.organisation) {
      return (
        null !== this.me.organisation.users_limit &&
        //@ts-ignore
        this.me.organisation.active_users_count >=
          this.me.organisation.users_limit
      );
    }

    return false;
  }

  protected async created(): Promise<void> {
    await this.paginate();
    if (this.hasAccessToOrgRequests) {
      await this.fetchRequests(this.orgRequestSearchParams);
    }
  }

  protected async switchTab(tab: number): Promise<void> {
    this.activeTab = tab;
    await this.paginate();
  }

  protected openForm(): void {
    if (this.limitReached) {
      if (this.me.organisation?.subscription_product_slug === "enterprise") {
        this.showAccessModal = true;
      } else {
        this.showForm = true;
        this.typeModal = "create";
      }
    } else {
      this.showForm = true;
      this.typeModal = "create";
    }
  }

  @Watch("userSearchParams", { deep: true })
  private async paginate(): Promise<void> {
    this.loading = true;

    switch (this.activeTab) {
      case 1:
        this.removeNullValues();
        await this.fetchUsers(this.userSearchParams);
        this.pagination = this.usersMeta;
        break;
      case 2:
        await this.fetchRequests(this.orgRequestSearchParams);
        this.pagination = this.orgRequestsMeta;
        break;
    }

    this.loading = false;
  }

  private removeNullValues(): void {
    this.userSearchParams.locations_ids?.forEach(
      (id: number | null, i: number): void => {
        if (!id) {
          this.userSearchParams.locations_ids?.splice(i, 1);
        }
      }
    );
  }

  protected async setPage(page: number): Promise<void> {
    switch (this.activeTab) {
      case 1:
        this.userSearchParams.page = page;
        break;
      case 2:
        this.orgRequestSearchParams.page = page;
        break;
    }

    await this.paginate();
  }

  protected async sortColumn(column: string): Promise<void> {
    switch (this.activeTab) {
      case 1:
        this.userSearchParams = this.sort(column, this.userSearchParams);
        break;
      case 2:
        this.orgRequestSearchParams = this.sort(
          column,
          this.orgRequestSearchParams
        );
        break;
    }

    await this.paginate();
  }

  @Debounce(350)
  protected async searchRecords(search: string): Promise<void> {
    const searchParams =
      this.activeTab === 1
        ? this.userSearchParams
        : this.orgRequestSearchParams;

    const params = this.search(search, searchParams);

    if (!!params) {
      switch (this.activeTab) {
        case 1:
          this.userSearchParams = params;
          break;
        case 2:
          this.orgRequestSearchParams = params;
          break;
      }
    }

    await this.paginate();
  }

  protected async closeForm(submitted: boolean): Promise<void> {
    this.showForm = false;
    this.selectedUser = null;
    this.notificationAboutAddNewUserData = null;
    this.isOpenNotificationModal = false;

    if (submitted) {
      await this.paginate();
    }
  }

  protected async closeModal(submitted: boolean): Promise<void> {
    this.showDeleteModal = false;
    this.selectedUser = null;

    if (submitted) {
      await this.paginate();
    }
  }

  protected update(user: User): void {
    this.selectedUser = user;
    this.showForm = true;
    this.typeModal = "update";
  }

  protected remove(user: User): void {
    this.selectedUser = user;
    this.showDeleteModal = true;
  }

  protected async allow(request: OrganisationRequest): Promise<void> {
    this.loading = true;

    try {
      await this.allowUser({
        userId: request.user_id,
        host: window.location.origin,
      });
      await this.paginate();
      await this.$store.dispatch(NotificationActions.PUSH_NOTIFICATION, {
        text: this.$t("app.views.users.userAllowed").toString(),
        type: NotificationType.SUCCESS,
      });
    } catch (exception: any) {
      await this.$store.dispatch(NotificationActions.PUSH_NOTIFICATION, {
        text: exception.response.data.message,
        type: NotificationType.DANGER,
      });
    }

    this.loading = false;
  }

  protected async deny(request: OrganisationRequest): Promise<void> {
    this.loading = true;

    try {
      await this.denyUser({
        userId: request.user_id,
        host: window.location.origin,
      });
      await this.paginate();
      await this.$store.dispatch(NotificationActions.PUSH_NOTIFICATION, {
        text: this.$t("app.views.users.userDenied").toString(),
        type: NotificationType.SUCCESS,
      });
    } catch (exception: any) {
      await this.$store.dispatch(NotificationActions.PUSH_NOTIFICATION, {
        text: exception.response.data.message,
        type: NotificationType.DANGER,
      });
    }

    this.loading = false;
  }
}
